// constants.js
export const FORMSPREE_ENDPOINT = "https://formspree.io/f/xnjjkpvn";

export const STEPS = [
  { id: 1, title: "Project Type", icon: "Film" },
  { id: 2, title: "Project Details", icon: "FileText" },
  { id: 3, title: "Schedule", icon: "Calendar" },
  { id: 4, title: "Investment", icon: "DollarSign" },
  { id: 5, title: "Confirmation", icon: "CheckCircle" },
];

export const CONSULTATION_TYPES = [
  {
    id: "virtual",
    title: "Virtual Consultation",
    description:
      "Video call via Zoom or Google Meet - perfect for initial discussions and project planning.",
    icon: "Video",
    duration: "45-60 minutes",
    features: [
      "Screen sharing",
      "Recording available",
      "No travel required",
      "Flexible scheduling",
    ],
  },
  {
    id: "in-person",
    title: "In-Person Meeting",
    description:
      "Meet at our studio or your office to discuss your project in detail with portfolio viewing.",
    icon: "Users",
    duration: "60-90 minutes",
    features: [
      "Portfolio viewing",
      "Equipment showcase",
      "Location assessment",
      "Team introductions",
    ],
  },
  {
    id: "on-site",
    title: "On-Site Visit",
    description:
      "We visit your location to assess the space and discuss project requirements in context.",
    icon: "MapPin",
    duration: "90-120 minutes",
    features: [
      "Location scouting",
      "Technical assessment",
      "Lighting evaluation",
      "Logistics planning",
    ],
  },
];

export const TIME_SLOTS = [
  { value: "09:00", label: "9:00 AM", available: true },
  { value: "10:00", label: "10:00 AM", available: true },
  { value: "11:00", label: "11:00 AM", available: true },
  { value: "13:00", label: "1:00 PM", available: true },
  { value: "14:00", label: "2:00 PM", available: true },
  { value: "15:00", label: "3:00 PM", available: true },
  { value: "17:00", label: "5:00 PM", available: true },
];

export const BUDGET_OPTIONS = [
  {
    id: "under-20k",
    range: "Under KES 20,000",
    minValue: 0,
    title: "Starter Package",
    description: "Entry-level support for focused, low-scope projects",
    includes: [
      "Short production window",
      "Minimal crew",
      "Basic post-production",
      "Single deliverable",
    ],
    bestFor: "Quick edits, simple shoots, consultation-led work",
  },
  {
    id: "20k-50k",
    range: "KES 20,000 – 50,000",
    minValue: 20000,
    title: "Essential Package",
    description: "Balanced solution for small but complete productions",
    includes: [
      "Half-day production",
      "Standard crew setup",
      "Core post-production",
      "Primary deliverable",
    ],
    bestFor: "Portraits, small brand videos, short events",
  },
  {
    id: "50k-75k",
    range: "KES 50,000 – 75,000",
    minValue: 50000,
    title: "Professional Package",
    description: "Comprehensive coverage with elevated production quality",
    includes: [
      "Full-day production",
      "Advanced post-production",
      "Multiple deliverables",
      "Format optimization",
    ],
    bestFor: "Brand storytelling, interviews, campaigns",
    popular: true,
  },
  {
    id: "75k-100k",
    range: "KES 75,000 – 100,000",
    minValue: 75000,
    title: "Premium Package",
    description: "High-impact production with creative direction",
    includes: [
      "Extended production time",
      "Creative direction",
      "Premium post-production",
      "Multiple formats & versions",
    ],
    bestFor: "Launches, executive branding, polished campaigns",
  },
  {
    id: "100k-500k",
    range: "KES 100,000 – 500,000",
    minValue: 100000,
    title: "Advanced Production",
    description: "Full-scale production for complex projects",
    includes: [
      "Multi-day production",
      "Full creative team",
      "Advanced logistics",
      "Expanded usage rights",
    ],
    bestFor: "Large events, documentaries, multi-location shoots",
  },
  {
    id: "500k-plus",
    range: "KES 500,000+",
    minValue: 500000,
    title: "Enterprise Solution",
    description: "Custom-built solutions for large-scale or ongoing work",
    includes: [
      "Unlimited scope",
      "Dedicated production team",
      "Strategic planning",
      "Long-term partnership support",
    ],
    bestFor: "National campaigns, retained engagements",
  },
  {
    id: "discuss",
    minValue: 0,
    range: "Custom / To Be Discussed",
    title: "Flexible Engagement",
    description: "Scope and pricing defined collaboratively",
    includes: [
      "Needs assessment",
      "Custom scoping",
      "Strategic guidance",
      "Tailored quotation",
    ],
    bestFor: "Undefined or evolving requirements",
  },
];

export const BUDGET_FACTORS = [
  {
    id: "crew-size",
    label: "Crew Size",
    description: "Number of team members required",
  },
  {
    id: "equipment",
    label: "Equipment Complexity",
    description: "Specialized cameras, drones, lighting",
  },
  {
    id: "locations",
    label: "Multiple Locations",
    description: "Travel and setup at various sites",
  },
  {
    id: "duration",
    label: "Production Duration",
    description: "Number of shooting days required",
  },
  {
    id: "post-production",
    label: "Post-Production Scope",
    description: "Editing, color grading, motion graphics",
  },
  {
    id: "deliverables",
    label: "Multiple Deliverables",
    description: "Various formats and versions",
  },
  {
    id: "timeline",
    label: "Rush Timeline",
    description: "Expedited delivery requirements",
  },
  {
    id: "usage-rights",
    label: "Extended Usage Rights",
    description: "Broad distribution and licensing",
  },
];

export const PAYMENT_OPTIONS = [
  {
    id: "milestone",
    title: "Milestone Payments",
    description: "Pay in phases aligned with project milestones",
    structure: "30% deposit, 40% at production start, 30% on delivery",
    benefits: [
      "Cash flow friendly",
      "Progress-based payments",
      "Risk mitigation",
    ],
  },
  {
    id: "upfront",
    title: "Upfront Payment",
    description: "Single payment before project commencement",
    structure: "100% payment upon contract signing",
    benefits: ["Project priority", "5% discount applied", "Simplified billing"],
  },
  {
    id: "net-terms",
    title: "Net Terms",
    description: "Corporate payment terms for established clients",
    structure: "Net 30 payment terms after delivery",
    benefits: [
      "Standard corporate process",
      "Credit approval required",
      "Relationship-based",
    ],
  },
];

// export const PROJECT_TYPES = [
//   {
//     id: "visual-production",
//     title: "Visual Production",
//     description:
//       "High-end visual content creation designed to elevate brand perception and capture attention across platforms.",
//     icon: "Video",
//     features: [
//       "Videography & Cinematic Films",
//       "Professional Photography",
//       "Drone Videography & Photography",
//       "Event Coverage & Documentation",
//     ],
//     duration: "1 day – 6 weeks",
//     investment: "From KES 50,000",
//     link: "/services#visual-production",
//   },
//   {
//     id: "branding",
//     title: "Strategic Branding",
//     description:
//       "Strategic brand development that defines identity, positioning, and consistency across all touchpoints.",
//     icon: "Layers",
//     features: [
//       "Brand Strategy & Positioning",
//       "Visual Identity Design",
//       "Messaging Framework",
//       "Brand Guidelines",
//     ],
//     duration: "3–6 weeks",
//     investment: "From KES 150,000",
//     link: "/services#strategic-branding",
//   },
//   {
//     id: "digital-presence",
//     title: "Website & Digital Presence",
//     description:
//       "Purpose-built digital platforms that establish credibility and drive conversion.",
//     icon: "Globe",
//     features: [
//       "Website Strategy",
//       "UX/UI Design",
//       "Website Development",
//       "SEO & Performance Optimization",
//     ],
//     duration: "4–8 weeks",
//     investment: "From KES 120,000",
//     link: "/services#digital-presence",
//   },
//   {
//     id: "social-media-presence",
//     title: "Social Media Presence",
//     description:
//       "Consistent and strategic content systems that grow audience, engagement, and brand authority.",
//     icon: "Share2",
//     features: [
//       "Social Media Strategy",
//       "Content Planning & Calendars",
//       "Content Creation",
//       "Account Management & Reporting",
//     ],
//     duration: "Monthly Retainer",
//     investment: "From KES 30,000 / month",
//     link: "/services#social-media",
//   },
//   {
//     id: "promotional-advertising",
//     title: "Promotional Campaigns & Advertising",
//     description:
//       "Integrated promotional campaigns engineered for visibility, conversion, and measurable results.",
//     icon: "Megaphone",
//     features: [
//       "Campaign Strategy & Concepting",
//       "Creative Production",
//       "Paid Advertising Management",
//       "Performance Tracking & Optimization",
//     ],
//     duration: "4–8 weeks or Retainer",
//     investment: "From KES 200,000",
//     link: "/services#campaign-engineering",
//   },
// ];
// constants.js
// export const MIN_BUDGET_BY_PROJECT = {
//   "visual-production": "50k-75k",
//   branding: "150k-plus", 
//   "digital-presence": "50k-75k",
//   "social-media-presence": "under-20k", 
//   "promotional-advertising": "200k-plus",
// };

export const INDUSTRY_OPTIONS = [
  "Technology",
  "Finance & Banking",
  "Healthcare",
  "Real Estate",
  "Manufacturing",
  "Consulting",
  "Retail",
  "Other",
];

export const TIMELINE_OPTIONS = [
  { value: "asap", label: "ASAP (Rush Project)" },
  { value: "1-2-weeks", label: "1-2 Weeks" },
  { value: "3-4-weeks", label: "3-4 Weeks" },
  { value: "1-2-months", label: "1-2 Months" },
  { value: "3-6-months", label: "3-6 Months" },
  { value: "flexible", label: "Flexible Timeline" },
];
